import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Checkbox,
  ListItemText,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Menu,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
  UpdateData,
  DocumentData,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import { db } from '../../config/config';
import Header from '../Hub/components/Header';
import PageHeading from '../../components/PageHeading';
import StandaloneBackButton from '../../components/StandaloneBackButton';
import { filterPanel, posterGrid } from '../../utils/surfaces';
import { gradients } from '../../theme/colors';
import useHomeScreenApp from '../../utils/useHomeScreenApp';
import TVShowCard from './components/TVShowCard';
import AddTVShowModal from './components/AddTVShowModal';
import EpisodesDialog from './components/EpisodesDialog';
import ConfirmDeleteTVShowModal from './components/ConfirmDeleteTVShowModal';
import TrailerDialog from './components/TrailerDialog';
import {
  COLLECTION_NAME,
  EMPTY_MESSAGES,
  SHOW_STATUSES,
  STATUS_LABELS,
  ShowStatus,
  TVShow,
  deriveStatus,
  hasEnded,
  isCaughtUp,
  newEpisodesBadge,
} from './types';
import {
  TmdbSearchResult,
  fetchShowSummary,
  imdbUrl,
  posterUrl,
} from './tmdb';

const DEFAULT_STATUSES: ShowStatus[] = ['inProgress'];
const PAGE_TITLE = 'TV Watchlist';
// Ended shows rarely come back, so they're only re-checked weekly.
const ENDED_RECHECK_MS = 7 * 24 * 60 * 60 * 1000;
const REFRESH_CONCURRENCY = 5;

// Filter chip look: gradient when on, quiet outline when off.
const chipSx = (selected: boolean) =>
  selected
    ? {
        backgroundImage: gradients.primary,
        color: '#ffffff',
        fontWeight: 700,
        '& .MuiChip-icon': { color: '#ffffff' },
        '&:hover': { filter: 'brightness(1.08)' },
      }
    : {
        borderColor: 'divider',
        color: 'text.primary',
        '& .MuiChip-icon': { color: 'primary.main' },
      };

const orderStatuses = (statuses: ShowStatus[]) =>
  SHOW_STATUSES.filter((status) => statuses.includes(status));

const latestAiredSeason = (show: TVShow) =>
  Math.max(0, ...show.tmdb.seasons.filter((s) => s.aired > 0).map((s) => s.n));

const checkedAgo = (millis: number) => {
  const minutes = Math.round((Date.now() - millis) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

// Runs `task` over `items`, a few at a time, so TMDB isn't hit all at once.
const runLimited = async <T,>(items: T[], task: (item: T) => Promise<void>) => {
  const queue = [...items];
  const worker = async () => {
    while (queue.length) await task(queue.shift() as T);
  };
  await Promise.all(Array.from({ length: REFRESH_CONCURRENCY }, worker));
};

const TVShows = () => {
  useHomeScreenApp('/manifest-tv.json', PAGE_TITLE);

  const [shows, setShows] = useState<TVShow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedStatuses, setSelectedStatuses] =
    useState<ShowStatus[]>(DEFAULT_STATUSES);
  const [newOnly, setNewOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedShow, setSelectedShow] = useState<TVShow | null>(null);
  const [openShowId, setOpenShowId] = useState<string | null>(null);
  const [trailerShow, setTrailerShow] = useState<TVShow | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  /* Anonymous visitors are read-only */
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) => {
      setIsReadOnly(!user);
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchShows = async () => {
      try {
        const snapshot = await getDocs(collection(db, COLLECTION_NAME));
        setShows(
          snapshot.docs.map((entry) => ({
            id: entry.id,
            ...entry.data(),
          })) as TVShow[]
        );
      } catch (err) {
        console.error(err);
        toast.error('Failed to load your TV shows');
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
  }, []);

  /* ─────────────────────────────
   * Refresh from TMDB. Only the owner can write, so visitors see the stored
   * summary. Returns how many shows you'd caught up on have new episodes.
   * ───────────────────────────── */
  const refreshShows = async (targets: TVShow[]) => {
    let withNew = 0;
    await runLimited(targets, async (show) => {
      try {
        const tmdb = await fetchShowSummary(show.tmdbId);
        await updateDoc(doc(db, COLLECTION_NAME, show.id), { tmdb });
        const before = show.tmdb.lastAirDate;
        if (
          tmdb.lastAirDate &&
          (!before || tmdb.lastAirDate > before) &&
          isCaughtUp(show)
        ) {
          withNew += 1;
        }
        setShows((prev) =>
          prev.map((entry) => (entry.id === show.id ? { ...entry, tmdb } : entry))
        );
      } catch (err) {
        console.error(`TMDB refresh failed for ${show.title}`, err);
      }
    });
    return withNew;
  };

  const newEpisodesMessage = (count: number) =>
    `${count} ${count === 1 ? 'show has' : 'shows have'} new episodes`;

  // Opening the page re-checks shows still airing once per calendar day, and
  // ended ones weekly. The stamp is each show's tmdb.checkedAt, so it holds
  // across devices. The Refresh button ignores it.
  const autoRefreshStarted = useRef(false);
  useEffect(() => {
    if (loading || !authChecked || isReadOnly || autoRefreshStarted.current) {
      return;
    }
    autoRefreshStarted.current = true;

    const stale = shows.filter((show) => {
      const checked = show.tmdb?.checkedAt?.toMillis?.();
      if (!checked) return true;
      return hasEnded(show)
        ? Date.now() - checked > ENDED_RECHECK_MS
        : new Date(checked).toDateString() !== new Date().toDateString();
    });
    if (stale.length === 0) return;

    setRefreshing(true);
    refreshShows(stale)
      .then((count) => {
        if (count > 0) toast.info(newEpisodesMessage(count));
      })
      .finally(() => setRefreshing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, authChecked, isReadOnly, shows]);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const count = await refreshShows(shows);
      toast.success(
        count > 0 ? newEpisodesMessage(count) : 'Up to date — no new episodes'
      );
    } finally {
      setRefreshing(false);
    }
  };

  const lastChecked = useMemo(
    () =>
      Math.max(0, ...shows.map((show) => show.tmdb?.checkedAt?.toMillis?.() ?? 0)),
    [shows]
  );

  /* Filters */
  const statuses = useMemo(() => {
    const map = new Map<string, ShowStatus>();
    shows.forEach((show) => map.set(show.id, deriveStatus(show)));
    return map;
  }, [shows]);

  const counts = useMemo(() => {
    const result = Object.fromEntries(
      SHOW_STATUSES.map((status) => [status, 0])
    ) as Record<ShowStatus, number>;
    statuses.forEach((status) => {
      result[status] += 1;
    });
    return result;
  }, [statuses]);

  const newCount = useMemo(
    () => shows.filter((show) => newEpisodesBadge(show)).length,
    [shows]
  );

  // Searching looks through every show, whatever the chips say, and the
  // New episodes toggle shows every badged show, whatever its status.
  const query = search.trim().toLowerCase();
  const visibleShows = useMemo(
    () =>
      shows
        .filter((show) =>
          query
            ? show.title.toLowerCase().includes(query)
            : newOnly
            ? newEpisodesBadge(show) !== null
            : selectedStatuses.includes(statuses.get(show.id) as ShowStatus)
        )
        .sort((a, b) => a.order - b.order),
    [shows, statuses, selectedStatuses, newOnly, query]
  );

  const isDefaultFilters =
    !query &&
    !newOnly &&
    selectedStatuses.length === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every((status) => selectedStatuses.includes(status));

  const clearFilters = () => {
    setSelectedStatuses(DEFAULT_STATUSES);
    setNewOnly(false);
    setSearch('');
  };

  const applyStatuses = (next: ShowStatus[]) => {
    if (next.length === 0) return;
    setNewOnly(false);
    setSelectedStatuses(orderStatuses(next));
  };

  const toggleStatus = (status: ShowStatus) => {
    if (!selectedStatuses.includes(status)) {
      applyStatuses([...selectedStatuses, status]);
    } else if (selectedStatuses.length > 1) {
      applyStatuses(selectedStatuses.filter((s) => s !== status));
    }
  };

  const handleStatusesSelect = (event: SelectChangeEvent<ShowStatus[]>) => {
    const { value } = event.target;
    applyStatuses(
      (typeof value === 'string' ? value.split(',') : value) as ShowStatus[]
    );
  };

  const emptyMessage = query
    ? `No shows match "${search.trim()}"`
    : newOnly
    ? 'Nothing new since you caught up.'
    : selectedStatuses.length === 1
    ? EMPTY_MESSAGES[selectedStatuses[0]]
    : shows.length === 0
    ? 'No TV shows yet.'
    : 'No shows match these filters.';

  /* ─────────────────────────────
   * CRUD
   * ───────────────────────────── */

  // New shows go to the top.
  const topOrder = () =>
    shows.length ? Math.min(...shows.map((show) => show.order)) - 1 : 0;

  const handleAddShow = async (result: TmdbSearchResult) => {
    if (shows.some((show) => show.tmdbId === result.id)) {
      toast.error(`"${result.name}" is already on your list`);
      return;
    }

    try {
      const entry: Omit<TVShow, 'id'> = {
        tmdbId: result.id,
        title: result.name,
        year: result.first_air_date
          ? parseInt(result.first_air_date.split('-')[0], 10)
          : 0,
        image: posterUrl(result.poster_path, 'w342'),
        order: topOrder(),
        dateAdded: Timestamp.now(),
        watched: {},
        rewatches: 0,
        tmdb: await fetchShowSummary(result.id),
      };
      const ref = await addDoc(collection(db, COLLECTION_NAME), entry);
      setShows((prev) => [...prev, { ...entry, id: ref.id }]);
      toast.success(`Added "${result.name}"`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to add show');
    }
  };

  const patchShow = async (
    show: TVShow,
    changes: Partial<TVShow>,
    successMessage?: string
  ) => {
    try {
      await updateDoc(doc(db, COLLECTION_NAME, show.id), changes);
      setShows((prev) =>
        prev.map((entry) => (entry.id === show.id ? { ...entry, ...changes } : entry))
      );
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update show');
    }
  };

  // Ticks update the card straight away, then save; a failed save puts the
  // show back as it was.
  const handleSetTicks = async (show: TVShow, ticks: Record<number, number[]>) => {
    const current = shows.find((entry) => entry.id === show.id) ?? show;
    const watched = { ...current.watched };
    const changes: UpdateData<DocumentData> = {};
    Object.entries(ticks).forEach(([season, episodes]) => {
      watched[season] = episodes;
      changes[`watched.${season}`] = episodes;
    });

    const next: TVShow = { ...current, watched };
    if (isCaughtUp(next)) {
      next.caughtUpThrough = {
        season: latestAiredSeason(next),
        airDate: next.tmdb.lastAirDate,
      };
      changes.caughtUpThrough = next.caughtUpThrough;
      if (next.rewatching) {
        next.rewatching = false;
        changes.rewatching = false;
      }
    }

    setShows((prev) => prev.map((entry) => (entry.id === show.id ? next : entry)));

    try {
      await updateDoc(doc(db, COLLECTION_NAME, show.id), changes);
      if (current.rewatching && !next.rewatching) {
        toast.success(`Finished rewatching "${show.title}"`);
      }
    } catch (err) {
      console.error(err);
      setShows((prev) =>
        prev.map((entry) => (entry.id === show.id ? current : entry))
      );
      toast.error('Failed to save your ticks');
    }
  };

  const handleToggleGivenUp = () => {
    if (!selectedShow) return;
    const givenUp = !selectedShow.givenUp;
    patchShow(
      selectedShow,
      { givenUp },
      givenUp
        ? `Gave up on "${selectedShow.title}"`
        : `Back to watching "${selectedShow.title}"`
    );
    closeMenu();
  };

  const handleStartRewatch = () => {
    if (!selectedShow) return;
    patchShow(
      selectedShow,
      {
        watched: {},
        rewatching: true,
        rewatches: (selectedShow.rewatches ?? 0) + 1,
        givenUp: false,
      },
      `Started rewatching "${selectedShow.title}"`
    );
    closeMenu();
  };

  const handleOpenImdb = async () => {
    if (!selectedShow) return;
    closeMenu();
    window.location.href = await imdbUrl(selectedShow.tmdbId, selectedShow.title);
  };

  const handleDelete = async () => {
    if (!selectedShow) return;
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, selectedShow.id));
      setShows((prev) => prev.filter((show) => show.id !== selectedShow.id));
      toast.success(`Removed "${selectedShow.title}"`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove show');
    }
    setShowDelete(false);
  };

  /* Menu */
  const handleMenuClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    show: TVShow
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedShow(show);
  };

  const closeMenu = () => setAnchorEl(null);

  const selectedStatus = selectedShow ? deriveStatus(selectedShow) : null;
  const openShow = shows.find((show) => show.id === openShowId) ?? null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />

      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        <StandaloneBackButton />
        <PageHeading>{PAGE_TITLE}</PageHeading>

        <Box
          sx={{
            p: 2,
            mb: 3,
            ...filterPanel(theme),
            display: 'grid',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1.5,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr) auto',
              md: 'auto 1fr auto',
            },
            gridTemplateAreas: {
              xs: `"search search" "states states" "new checked" "add actions"`,
              md: `"states search add" "new checked actions"`,
            },
          }}
        >
          {isMobile ? (
            <FormControl
              size="small"
              sx={{ gridArea: 'states', minWidth: 0, opacity: newOnly || query ? 0.5 : 1 }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={selectedStatuses}
                label="Status"
                onChange={handleStatusesSelect}
                renderValue={(selected) =>
                  selected.map((status) => STATUS_LABELS[status]).join(', ')
                }
              >
                {SHOW_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox
                      size="small"
                      checked={selectedStatuses.includes(status)}
                    />
                    <ListItemText
                      primary={STATUS_LABELS[status]}
                      secondary={`${counts[status]}`}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box
              sx={{
                gridArea: 'states',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                opacity: newOnly || query ? 0.5 : 1,
              }}
            >
              {SHOW_STATUSES.map((status) => {
                const selected = selectedStatuses.includes(status);
                return (
                  <Chip
                    key={status}
                    label={`${STATUS_LABELS[status]} ${counts[status]}`}
                    clickable
                    onClick={() => toggleStatus(status)}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={chipSx(selected)}
                  />
                );
              })}
            </Box>
          )}

          <Chip
            icon={<AutoAwesomeIcon fontSize="small" />}
            label={`New episodes ${newCount}`}
            clickable
            onClick={() => setNewOnly((on) => !on)}
            variant={newOnly ? 'filled' : 'outlined'}
            sx={{
              gridArea: 'new',
              justifySelf: 'start',
              opacity: query ? 0.5 : 1,
              ...chipSx(newOnly),
            }}
          />

          <TextField
            size="small"
            placeholder="Search shows…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              gridArea: 'search',
              justifySelf: { xs: 'stretch', md: 'end' },
              width: { xs: '100%', md: 280 },
            }}
            slotProps={{
              htmlInput: { 'aria-label': 'Search shows' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="Clear search"
                      onClick={() => setSearch('')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridArea: 'checked', justifySelf: 'end', textAlign: 'right' }}
          >
            {refreshing
              ? 'Checking for new episodes…'
              : lastChecked > 0
              ? `Checked ${checkedAgo(lastChecked)}`
              : ''}
          </Typography>

          <Box sx={{ gridArea: 'actions', display: 'flex', gap: 1, justifySelf: 'end' }}>
            {!isReadOnly && (
              <Button
                variant="text"
                color="inherit"
                disabled={refreshing || shows.length === 0}
                onClick={handleRefreshAll}
                startIcon={
                  refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
                }
                sx={{ whiteSpace: 'nowrap' }}
              >
                Refresh
              </Button>
            )}
            <Button
              variant="text"
              color="inherit"
              disabled={isDefaultFilters}
              onClick={clearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          </Box>

          {!isReadOnly && (
            <Button
              variant="contained"
              onClick={() => setShowAdd(true)}
              sx={{ gridArea: 'add', whiteSpace: 'nowrap' }}
            >
              Add Show
            </Button>
          )}
        </Box>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : visibleShows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">{emptyMessage}</Typography>
          </Box>
        ) : (
          <Box sx={posterGrid}>
                {visibleShows.map((show) => (
                  <TVShowCard
                    key={show.id}
                    show={show}
                    status={statuses.get(show.id) as ShowStatus}
                    isReadOnly={isReadOnly}
                    onOpen={(target) => setOpenShowId(target.id)}
                    onMenuClick={handleMenuClick}
                    onTrailer={setTrailerShow}
                  />
                ))}
              </Box>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center' }}
        >
          Showing {visibleShows.length} {visibleShows.length === 1 ? 'show' : 'shows'}
        </Typography>
      </Container>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={handleToggleGivenUp}>
          {selectedShow?.givenUp ? 'Resume watching' : 'Mark as given up'}
        </MenuItem>

        {(selectedStatus === 'completed' || selectedStatus === 'caughtUp') && (
          <MenuItem onClick={handleStartRewatch}>Start rewatch</MenuItem>
        )}

        <MenuItem onClick={handleOpenImdb}>Open on IMDb</MenuItem>

        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setShowDelete(true);
            closeMenu();
          }}
        >
          Remove
        </MenuItem>
      </Menu>

      <AddTVShowModal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        onAddShow={handleAddShow}
      />

      <EpisodesDialog
        show={openShow}
        isReadOnly={isReadOnly}
        onClose={() => setOpenShowId(null)}
        onSetTicks={handleSetTicks}
      />

      <TrailerDialog show={trailerShow} onClose={() => setTrailerShow(null)} />

      <ConfirmDeleteTVShowModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onConfirm={handleDelete}
        showTitle={selectedShow?.title || ''}
      />

      <ToastContainer />
    </Box>
  );
};

export default TVShows;
